import { NormalizedDisclosure } from "~/types/search.types";
import { SearchActions } from "~/types/actions.enum";
import { LabelTranslation } from "~/types";

// ✅ Initial states
export const initialState: TDisclosureState = {
    disclosures: [],
    disclosure: null,
    isAllDisclosuresAvailable: null
};

// ✅ Types

// Action type defn
export type DisclosureAction =
    | { type: SearchActions.SET_DISCLOSURE_ITEMS; payload: NormalizedDisclosure[] }
    | { type: SearchActions.SET_ACTIVE_DISCLOSURE; payload: NormalizedDisclosure | null }
    | { type: SearchActions.SET_ACTIVE_DISCLOSURE_BY_KEY; payload: TSetActiveDisclosure }
    | { type: SearchActions.SET_IS_ALL_DISCLOSURES; payload: NormalizedDisclosure | null }
    | { type: SearchActions.SET_DISCLOSURE_CATEGORY; payload: IDisclosureCategoryCount }
    | { type: SearchActions.RESET_DISCLOSURE_CATEGORIES; payload: boolean };

// State type defns
export type TDisclosureState = {
    /** List of normalized Disclosures `NormalizedDisclosure[]` */
    disclosures: NormalizedDisclosure[];
    /** Active/selected Disclosure `NormalizedDisclosure` */
    disclosure: NormalizedDisclosure | null;
    /** Flag indicating the "All Disclosures" label is selected. This is not a real Disclosure item. Its a `NormalizedDisclosure` item with:
     * ```
     * {
     *    id: -1,
     *    exploreId: -1,
     *    label: { en: "All Disclosures",... },
     *    ...
     * }
     * ```
     */
    isAllDisclosuresAvailable: NormalizedDisclosure | null;
};

export type TSetActiveDisclosure = {
    [K in keyof NormalizedDisclosure]: {
        property: K;
        value: NormalizedDisclosure[K];
    };
}[keyof NormalizedDisclosure];

export interface IDisclosureCategoryCount {
    disclosureId: number;
    value: number;
    isSelected: boolean;
}

// Store type
export type TDisclosureMethods =
    | "dispatch"
    | "getDisclosureByProperty"
    | "getDisclosureIDs"
    | "getDisclosureName"
    | "reset";

export type TDisclosureStore = TDisclosureState & {
    /**
     * Sets the Disclosures state using a reducer pattern.
     * @param {DisclosureAction} action - `DisclosureAction` action to apply.
     * @example
     * ```
     * const dispatch = useDisclosureStore((state) => state.dispatch)
     * dispatch({ type: SearchActions.SET_DISCLOSURE_ITEMS, payload: disclosureItem })
     * ```
     */
    dispatch: (action: DisclosureAction) => void;
    /**
     * Retrieves Disclosures by property (key) and value.
     * @param property - The `NormalizedDisclosure` property to filter by.
     * @param value - The `property` value to match.
     * @example const filtered = getDisclosureByProperty("exploreId", 3)
     */
    getDisclosureByProperty: <K extends keyof NormalizedDisclosure>(
        property: K,
        value: NormalizedDisclosure[K]
    ) => NormalizedDisclosure | undefined;
    /**
     * Retrieves all Disclosure IDs `Disclosure.exploreId`
     * @example const disclosureIDs = getDisclosureIDs()
     */
    getDisclosureIDs: () => number[];
    /**
     * Retrieves the disclosure text name `Disclosure.label`
     * @param id - The disclosure id
     * @example const disclosureName = getDisclosureName()
     */
    getDisclosureName: (id: number) => LabelTranslation | null;
    /**
     * Resets the store to its initial state
     */
    reset: () => void;
};
